import { useRoute } from '@react-navigation/native';
import React, { useCallback } from 'react';

import { STRINGS } from '@/constants/strings';
import LeaveCircleContent from '@/features/guardian-player/components/leave-circle';
import { calculateAge, formatGuardianApiDate } from '@/features/guardian-player/utils';
import { useGuardianStackNavigation } from '@/hooks';
import type { GuardianStackScreenProps } from '@/navigations/types';
import { useRemovePlayerGuardianMutation } from '@/queries/use-remove-player-guardian-mutation';

const strings = STRINGS.guardianPlayer.leaveCircle;

const LeaveCircleScreen = () => {
  const navigation = useGuardianStackNavigation();
  const route = useRoute<GuardianStackScreenProps<'LeaveCircle'>['route']>();
  const { player, guardianUid, since } = route.params;

  const firstName = player.firstname?.trim() || strings.fallbackPlayerName;
  const fullName =
    [player.firstname, player.lastname].filter(Boolean).join(' ').trim() || firstName;

  const age = calculateAge(player.dob);
  const detailLine = (age != null ? strings.cardDetail : strings.cardDetailNoAge)
    .replace('{age}', String(age))
    .replace('{level}', String(player.level));
  const sinceDate = formatGuardianApiDate(since, 'MMM yyyy');
  const sinceLine = sinceDate ? strings.cardSince.replace('{date}', sinceDate) : undefined;

  const { mutateAsync: leave, isPending } = useRemovePlayerGuardianMutation(player.id);

  const handleLeave = useCallback(async () => {
    try {
      await leave(guardianUid);
      // Left the circle — the player is no longer visible, so drop back to the dashboard.
      navigation.navigate('GuardianDashboard');
    } catch {
      // The API error interceptor surfaces the failure; stay so the user can retry.
    }
  }, [leave, guardianUid, navigation]);

  return (
    <LeaveCircleContent
      screenTitle={strings.screenTitle.replace('{name}', firstName)}
      playerName={firstName}
      avatarUrl={player.avatarUrl}
      fullName={fullName}
      detailLine={detailLine}
      sinceLine={sinceLine}
      onBack={navigation.goBack}
      onLeave={handleLeave}
      leaving={isPending}
    />
  );
};

export default LeaveCircleScreen;
